// /privacy — Public Notice of Privacy Practices (HIPAA NPP)
// Displays the full NPP notice + one-line acknowledgment of receipt
// with patient name, DOB, contact + typed e-signature.

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Download, ShieldCheck } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NPP_EFFECTIVE_DATE = "January 1, 2026";

const NPP_SECTIONS = [
  {
    title: "Our commitment",
    body:
      "Let Go Wellness is required by law to protect the privacy of your health information, provide you this notice of our legal duties and privacy practices, notify you following a breach of unsecured protected health information, and follow the terms of the notice currently in effect. This notice describes how medical and mental-health information about you may be used and disclosed, and how you can get access to this information. Please review it carefully.",
  },
  {
    title: "Uses & disclosures for treatment, payment & operations",
    body:
      "Treatment — We use your health information to provide, coordinate, and manage your care. Example: a therapist and a prescribing psychiatrist within our practice may share information about your treatment plan. Payment — We use your health information to bill for services. Example: we send a claim to your insurance company including the dates of service, diagnosis, and CPT codes. Operations — We use your health information for internal operations such as clinical supervision, quality assurance, credentialing, training, and case consultation.",
  },
  {
    title: "Uses & disclosures that require your written authorization",
    body:
      "Except as described in this notice, we will not use or disclose your health information without your written authorization. Written authorization is required for: (a) most uses of psychotherapy notes, (b) marketing communications, and (c) any sale of your health information. You may revoke a signed authorization at any time in writing, except to the extent we have already relied on it.",
  },
  {
    title: "Uses & disclosures that do not require your authorization",
    body:
      "Federal and state law permit or require us to disclose your health information without your authorization for: public-health activities; suspected abuse, neglect, or domestic violence; health oversight (audits, investigations, inspections); judicial and administrative proceedings under a court order or subpoena; law-enforcement purposes when required by law; coroners, medical examiners, and funeral directors; organ or tissue donation; research approved by an institutional review board with appropriate privacy safeguards; to avert a serious threat to health or safety; specialized government functions such as military or national security; workers' compensation as authorized by law.",
  },
  {
    title: "Your rights",
    body:
      "You have the right to: (1) inspect and copy your health information — some limits apply to psychotherapy notes; (2) request an amendment if you believe it is incorrect or incomplete; (3) request an accounting of certain disclosures made by us; (4) request restrictions on how we use or disclose your information (we are not required to agree except when the request pertains to a service you paid for in full out of pocket); (5) request confidential communications through a specific channel or address; (6) receive a paper copy of this notice on request; (7) be notified following a breach of your unsecured protected health information. To exercise any of these rights, please contact our Privacy Officer at letgowellness1@gmail.com or 203-437-8193.",
  },
  {
    title: "Our responsibilities",
    body:
      "We are required by law to maintain the privacy and security of your protected health information, notify you promptly if a breach occurs that may have compromised the privacy or security of your information, follow the duties and privacy practices described in this notice, and give you a copy of this notice. We will not use or share your information other than as described here unless you tell us we can in writing.",
  },
  {
    title: "Changes to this notice",
    body:
      "We reserve the right to change this notice. We reserve the right to make the revised or changed notice effective for medical information we already have about you as well as any information we receive in the future. The current notice is posted at letgowelllness.com/privacy and copies are available at our clinic on request.",
  },
  {
    title: "Complaints",
    body:
      "If you believe your privacy rights have been violated, you may file a complaint with us by contacting our Privacy Officer at letgowellness1@gmail.com or 203-437-8193, or with the Secretary of the U.S. Department of Health and Human Services, Office for Civil Rights, 200 Independence Ave, S.W., Washington, D.C. 20201, or by calling 1-877-696-6775. We will not retaliate against you for filing a complaint.",
  },
];

export default function PrivacyPractices() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    patient_name: "",
    patient_dob: "",
    patient_email: "",
    patient_phone: "",
    typed_signature: "",
    minor_guardian_name: "",
    acknowledged: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k) => (e) =>
    setForm((prev) => ({
      ...prev,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.acknowledged) {
      toast.error("Please check the box to acknowledge receipt.");
      return;
    }
    setBusy(true);
    const t = toast.loading("Filing your acknowledgment…");
    try {
      const res = await axios.post(`${API_BASE}/privacy-acks/submit`, form);
      setDone(res.data);
      toast.success("Acknowledgment received. You can download a copy below.", { id: t });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    if (!done) return;
    const pdf = new jsPDF({ unit: "in", format: "letter" });
    const W = 8.5;
    const M = 0.7;
    let y = 0.8;

    pdf.setFont("times", "bold");
    pdf.setFontSize(20);
    pdf.text(brand, M, y);
    y += 0.3;
    pdf.setFont("times", "italic");
    pdf.setFontSize(11);
    pdf.setTextColor(120, 115, 105);
    pdf.text("Notice of Privacy Practices — Acknowledgment of Receipt", M, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.25;
    pdf.setFontSize(8);
    pdf.setTextColor(120, 115, 105);
    pdf.text(`Effective ${NPP_EFFECTIVE_DATE}`, M, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.3;
    pdf.setDrawColor(180, 170, 150);
    pdf.line(M, y, W - M, y);
    y += 0.3;

    for (const section of NPP_SECTIONS) {
      if (y > 10) { pdf.addPage(); y = 0.8; }
      pdf.setFont("times", "bold");
      pdf.setFontSize(11);
      pdf.text(section.title, M, y);
      y += 0.2;
      pdf.setFont("times", "normal");
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(section.body, W - 2 * M);
      pdf.text(lines, M, y);
      y += 0.16 * lines.length + 0.2;
    }

    // Acknowledgment
    if (y > 9) { pdf.addPage(); y = 0.8; }
    y += 0.2;
    pdf.line(M, y, W - M, y);
    y += 0.3;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("ACKNOWLEDGMENT OF RECEIPT", M, y);
    y += 0.3;
    pdf.setFont("times", "normal");
    pdf.setFontSize(10);
    const ackLines = pdf.splitTextToSize(
      `I, ${done.patient_name}, acknowledge that I have received a copy of the Let Go Wellness Notice of Privacy Practices.`,
      W - 2 * M
    );
    pdf.text(ackLines, M, y);
    y += 0.16 * ackLines.length + 0.3;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("PATIENT", M, y);
    pdf.text("DOB", M + 3.4, y);
    pdf.text("EMAIL", M + 4.8, y);
    y += 0.18;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(done.patient_name, M, y);
    pdf.text(done.patient_dob || "—", M + 3.4, y);
    pdf.text(done.patient_email, M + 4.8, y);
    y += 0.3;

    if (done.minor_guardian_name) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.text("GUARDIAN", M, y);
      y += 0.18;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(done.minor_guardian_name, M, y);
      y += 0.25;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("SIGNATURE", M, y);
    y += 0.25;
    pdf.setFont("times", "italic");
    pdf.setFontSize(14);
    pdf.text(done.typed_signature, M, y);
    y += 0.2;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 115, 105);
    pdf.text(`Signed electronically on ${new Date(done.signed_at).toLocaleString()}`, M, y);

    pdf.save(`privacy-ack-${(done.patient_name || "patient").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
  };

  if (done) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="privacy-success">
        <Navbar />
        <section className="pt-48 pb-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#3A5A40]/10 flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-[#3A5A40]" />
            </div>
            <p className="overline text-[#9F5C45] mb-3">Filed & received</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">
              Thank you, {done.patient_name.split(" ")[0]}.
            </h1>
            <p className="text-[#57534E] max-w-xl mx-auto mb-10">
              Your acknowledgment of the Notice of Privacy Practices is on file. Please
              download a copy for your records.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={downloadPdf}
                data-testid="privacy-download-pdf"
                className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
              >
                <Download className="w-4 h-4" /> Download signed copy
              </button>
              <button
                onClick={() => navigate("/")}
                data-testid="privacy-back-home"
                className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
              >
                Back to site
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="privacy-page">
      <Navbar />
      <section className="pt-48 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#9F5C45] hover:text-[#3A5A40]"
            data-testid="privacy-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <p className="overline text-[#9F5C45] mt-6 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> HIPAA
          </p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1C1917] mb-3">
            Notice of <em className="text-[#3A5A40]">Privacy Practices.</em>
          </h1>
          <p className="text-[#57534E] max-w-2xl">
            This notice describes how medical and mental-health information about you may be used and
            disclosed and how you can get access to this information. Please review carefully.
          </p>
          <p className="text-xs text-[#86827B] mt-3">Effective {NPP_EFFECTIVE_DATE}</p>

          <div className="mt-12 space-y-8">
            {NPP_SECTIONS.map((s) => (
              <div key={s.title} className="border border-[#E2DCD0] bg-white p-6" data-testid={`npp-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="overline text-[#9F5C45] mb-2">{s.title}</p>
                <p className="text-[#1C1917] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="mt-16 space-y-8 border-t border-[#E2DCD0] pt-10" data-testid="privacy-form">
            <p className="overline text-[#9F5C45]">Acknowledgment of receipt</p>
            <h2 className="font-serif text-2xl text-[#1C1917]">Sign to confirm you&apos;ve received this notice.</h2>

            <fieldset className="grid md:grid-cols-2 gap-6">
              <Input label="Full legal name" required value={form.patient_name} onChange={update("patient_name")} testId="patient_name" />
              <Input label="Date of birth" placeholder="MM / DD / YYYY" value={form.patient_dob} onChange={update("patient_dob")} testId="patient_dob" />
              <Input label="Email" type="email" required value={form.patient_email} onChange={update("patient_email")} testId="patient_email" />
              <Input label="Phone" type="tel" value={form.patient_phone} onChange={update("patient_phone")} testId="patient_phone" />
            </fieldset>

            <Input label="If patient is a minor (under 18), guardian name" value={form.minor_guardian_name} onChange={update("minor_guardian_name")} testId="minor_guardian_name" />

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.acknowledged}
                onChange={update("acknowledged")}
                data-testid="acknowledge"
                className="mt-1 w-4 h-4 accent-[#3A5A40]"
              />
              <span className="text-sm text-[#1C1917]">
                I acknowledge that I have received a copy of the {brand} Notice of Privacy Practices.
                <span className="text-[#9F5C45]"> Required.</span>
              </span>
            </label>

            <Input
              label="Electronic signature (type your full legal name)"
              required
              value={form.typed_signature}
              onChange={update("typed_signature")}
              testId="typed_signature"
              fontStyle="italic"
            />

            <div className="border-t border-[#E2DCD0] pt-6">
              <button
                type="submit"
                disabled={busy}
                data-testid="privacy-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 btn-primary text-xs uppercase tracking-[0.22em] disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Filing…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" /> Sign & acknowledge
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function Input({ label, className = "", required, testId, fontStyle, ...rest }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-[#57534E] mb-2 block">
        {label} {required && <span className="text-[#9F5C45]">*</span>}
      </span>
      <input
        required={required}
        data-testid={`privacy-input-${testId}`}
        style={fontStyle ? { fontStyle } : undefined}
        className="w-full border-0 border-b-2 border-[#D6CFC2] bg-transparent px-1 py-3 text-base focus:outline-none focus:border-[#3A5A40] transition-colors"
        {...rest}
      />
    </label>
  );
}
